import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchChequeTypes,
  createChequeType,
  updateChequeType,
  deleteChequeType,
} from "../../api/chequeMasterApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import { GlobalModal } from "../../../../global/GlobalModal";
import {
  GlobalTextField,
  GlobalButton,
  GlobalSelectField,
  GlobalTextAreaField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import GlobalFeedback from "../../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import { useChequeRole } from "../../utils/useChequeRole";
import { CHEQUE_ISSUES_SUBMODULE_SLUG } from "../../utils/chequePermissionSlugs";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const ChequeTypeMaster = ({ onMastersChange }) => {
  const perms = useChequeRole(CHEQUE_ISSUES_SUBMODULE_SLUG);
  const canManage = !perms.permissionsLoading && perms.canManageMasters;
  const canDelete = !perms.permissionsLoading && perms.canDeleteMasters;
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    type_name: "",
    description: "",
    status_id: 1,
  });
  const [formError, setFormError] = useState("");
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const scrollToForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchChequeTypes();
      setDataList(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError("");
  };

  const openCreate = () => {
    setEditing(false);
    setFormData({ id: null, type_name: "", description: "", status_id: 1 });
    setFormError("");
    setFormErrorDetails({});
    setSuccessMsg("");
    scrollToForm();
  };

  const openEdit = (item) => {
    setSuccessMsg("");
    setEditing(true);
    setFormData({
      id: item.id,
      type_name: item.type_name,
      description: item.description || "",
      status_id:
        item.status_id != null && item.status_id !== ""
          ? Number(item.status_id)
          : 1,
    });
    setFormError("");
    setFormErrorDetails({});
    scrollToForm();
  };

  const handleCancel = () => {
    if (submitting) return;
    setEditing(false);
    setFormData({ id: null, type_name: "", description: "", status_id: 1 });
    setFormError("");
    setFormErrorDetails({});
  };

  const resetAfterSave = () => {
    setEditing(false);
    setFormData({ id: null, type_name: "", description: "", status_id: 1 });
    setFormError("");
    setFormErrorDetails({});
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError("");
    setFormErrorDetails({});
    setSuccessMsg("");
    if (!editing && !formData.type_name.trim()) {
      setFormError("Type name is required.");
      return;
    }
    setSubmitting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      if (editing) {
        await updateChequeType(
          formData.id,
          {
            type_name: formData.type_name.trim(),
            description: formData.description.trim() || null,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        await createChequeType(
          {
            type_name: formData.type_name.trim(),
            description: formData.description.trim() || null,
          },
          csrfToken,
        );
      }
      setSuccessMsg(
        editing
          ? "Cheque type updated successfully."
          : "Cheque type added successfully.",
      );
      resetAfterSave();
      loadData();
      onMastersChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteChequeType(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Cheque type removed from the directory.");
      loadData();
      onMastersChange?.();
    } catch (err) {
      setError(err.message || "Failed to remove cheque type");
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const ALERT_ID = "ci-type-master-alert";

  if (loading) {
    return <p className="ub-loading">Loading cheque types…</p>;
  }

  return (
    <div className="ub-master-section">
      <h3 className="ub-section__subhead" style={{ margin: "0 0 1.25rem" }}>
        Cheque type master
      </h3>
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={error} />

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        {canManage ? (
        <GlobalFormShell
          legend={editing ? "Edit cheque type" : "Add cheque type"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {editing ? (
                <GlobalButton
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton
                variant="primary"
                onClick={handleSubmit}
                loading={submitting}
              >
                {editing ? "Save changes" : "Add type"}
              </GlobalButton>
            </>
          }
        >
          {editing ? (
            <>
              <GlobalTextField
                id="ochq_type_name_ro"
                label="Type Name"
                value={formData.type_name}
                onChange={(v) => setFormField("type_name", v)}
                baseAlertId={ALERT_ID}
                placeholder="e.g. Account payee, Bearer"
              />
              <GlobalTextField
                id="ochq_type_description_edit"
                label="Description"
                value={formData.description}
                onChange={(v) => setFormField("description", v)}
                baseAlertId={ALERT_ID}
                placeholder="When to use this cheque type"
              />
              <GlobalSelectField
                id="ochq_type_status_id"
                label="Status"
                value={String(formData.status_id)}
                onChange={(v) => setFormField("status_id", Number(v))}
                options={STATUS_OPTIONS}
                error={formErrorDetails.status_id}
                baseAlertId={ALERT_ID}
                placeholderOption="Select active or inactive…"
              />
            </>
          ) : (
            <>
              <GlobalTextField
                id="ochq_type_name"
                label="Type Name"
                value={formData.type_name}
                onChange={(v) => setFormField("type_name", v)}
                error={formErrorDetails.type_name}
                baseAlertId={ALERT_ID}
                required
                placeholder="e.g. Account payee, Bearer"
              />
              <GlobalTextField
                id="ochq_type_description"
                label="Description"
                value={formData.description}
                onChange={(v) => setFormField("description", v)}
                baseAlertId={ALERT_ID}
                placeholder="When to use this cheque type (optional)"
              />
            </>
          )}
        </GlobalFormShell>
        ) : (
          <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
            You have view-only access to cheque type master records.
          </p>
        )}
      </div>

      <GlobalDataTable
        tableAriaLabel="Cheque type master table"
        headers={[
          { key: "type_name", label: "Type name" },
          { key: "description", label: "Description" },
          { key: "status", label: "Status" },
          { key: "actions", label: "Actions", className: "ub-table__col-actions" },
        ]}
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
      >
          {dataList.length === 0 ? (
            <tr>
              <td
                colSpan={4}
                style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}
              >
                No cheque types yet. Add your first type above.
              </td>
            </tr>
          ) : (
            dataList.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 500 }}>{item.type_name}</td>
                <td style={{ fontSize: "0.875rem", color: "#64748b" }}>
                  {item.description || "—"}
                </td>
                <td>
                  <GlobalBadge active={Number(item.status_id) === 1} />
                </td>
                <td className="ub-table__col-actions">
                  {canManage ? (
                  <div className="ub-table-actions">
                    <GlobalTableActionButton action="edit" onClick={() => openEdit(item)} />
                    {canDelete ? (
                    <GlobalTableActionButton
                      action="delete"
                      onClick={() => {
                        setItemToDelete(item);
                        setShowDeleteConfirm(true);
                      }}
                    />
                    ) : null}
                  </div>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              variant="ghost"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove cheque type <b>{itemToDelete?.type_name}</b> from the directory?
          It will be hidden from lists.
        </p>
      </GlobalModal>
    </div>
  );
};

export default ChequeTypeMaster;
